import axios from 'axios';
import { API_BASE_URL } from './config';
import { buildOperationExpensePayload } from '../constants/operationService';
import {
  fetchAttachmentBlob,
  openAttachmentBlob,
  parseAttachmentDownloadResponse,
} from '../utils/clientAttachmentOpen';

const TOKEN_KEY = 'barbershop_token';
const GLOBAL_TOKEN_KEY = '__barbershop_token__';

if (typeof window !== 'undefined') {
  window.__AUTH_DEBUG__ = window.__AUTH_DEBUG__ || [];
}
const debug = (msg, data = {}) => {
  if (typeof window !== 'undefined') {
    const entry = { t: Date.now(), msg, ...data };
    (window.__AUTH_DEBUG__ = window.__AUTH_DEBUG__ || []).push(entry);
    if (window.__AUTH_DEBUG__.length > 100) window.__AUTH_DEBUG__.shift();
  }
  console.log('[AUTH]', msg, data);
};

function authHeaders() {
  const callStack = new Error().stack?.split('\n').slice(2, 5).join(' <- ') || '?';
  if (typeof window !== 'undefined') {
    const fromStorage = localStorage.getItem(TOKEN_KEY);
    debug('authHeaders: localStorage', { hasStorage: !!fromStorage, len: fromStorage?.length, preview: fromStorage?.slice(0, 20) });
    if (fromStorage) {
      window[GLOBAL_TOKEN_KEY] = fromStorage;
      api.defaults.headers.common['Authorization'] = `Bearer ${fromStorage}`;
    }
  }
  const t = getToken();
  const hasToken = !!t;
  debug('authHeaders: result', { hasToken, caller: callStack });
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function getToken() {
  try {
    if (typeof window === 'undefined') return null;
    if (window[GLOBAL_TOKEN_KEY]) return window[GLOBAL_TOKEN_KEY];
    const fromStorage = localStorage.getItem(TOKEN_KEY);
    if (fromStorage) window[GLOBAL_TOKEN_KEY] = fromStorage;
    return fromStorage;
  } catch (e) {
    debug('getToken ERROR', { err: String(e) });
    return null;
  }
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});
api.__instanceId = 'api_' + Math.random().toString(36).slice(2, 8);

api.interceptors.request.use((config) => {
  if (typeof window !== 'undefined') {
    const fromStorage = localStorage.getItem(TOKEN_KEY);
    if (fromStorage) window[GLOBAL_TOKEN_KEY] = fromStorage;
  }
  const token = getToken();
  const hadAuth = !!config.headers?.Authorization;
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  // FormData: не отправлять application/json — иначе Laravel не получит file
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    if (config.headers?.set) {
      config.headers.set('Content-Type', undefined);
    } else if (config.headers) {
      delete config.headers['Content-Type'];
      delete config.headers['content-type'];
    }
  }
  debug('interceptor REQUEST', {
    apiId: api.__instanceId,
    url: config.url,
    method: config.method,
    hasToken: !!token,
    hadAuth,
    finalAuth: !!config.headers?.Authorization,
    headersKeys: config.headers ? Object.keys(config.headers) : [],
  });
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      setStoredToken(null);
      if (window.location.pathname.startsWith('/admin')) {
        window.location.href = '/auth/sign-in';
      }
    }
    return Promise.reject(err);
  }
);

export const getStoredToken = getToken;
export const setStoredToken = (token) => {
  debug('setStoredToken', { hasToken: !!token, preview: token?.slice(0, 15), stack: new Error().stack?.split('\n').slice(2, 6).join(' <- ') });
  if (token) {
    if (typeof window !== 'undefined') window[GLOBAL_TOKEN_KEY] = token;
    localStorage.setItem(TOKEN_KEY, token);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    if (typeof window !== 'undefined') window[GLOBAL_TOKEN_KEY] = null;
    localStorage.removeItem(TOKEN_KEY);
    delete api.defaults.headers.common['Authorization'];
  }
};

// При загрузке модуля синхронизировать токен (важно при refresh и code-split)
if (typeof window !== 'undefined') {
  const existing = localStorage.getItem(TOKEN_KEY);
  debug('api module INIT', { hasExisting: !!existing, len: existing?.length });
  if (existing) {
    window[GLOBAL_TOKEN_KEY] = existing;
    api.defaults.headers.common['Authorization'] = `Bearer ${existing}`;
  }
}

/** Текст ошибки из ответа Laravel / axios для toast */
export function getApiErrorMessage(err, fallback = 'Неизвестная ошибка') {
  const data = err?.response?.data;
  if (typeof data === 'string' && data.trim()) {
    try {
      const parsed = JSON.parse(data);
      if (parsed?.message) return String(parsed.message);
    } catch {
      return data.slice(0, 500);
    }
  }
  if (data?.errors && typeof data.errors === 'object') {
    const flat = Object.values(data.errors).flat().filter(Boolean);
    if (flat.length) return flat.join('. ');
  }
  if (typeof data?.message === 'string' && data.message.trim()) {
    const msg = data.message.trim();
    if (/налогов/i.test(msg)) {
      return 'Недостаточно прав для этого действия';
    }
    if (!/^The .+ field is required\.?$/i.test(msg)) {
      return msg;
    }
  }
  if (err?.response?.status === 401) {
    return 'Сессия истекла — войдите снова';
  }
  if (err?.response?.status === 422 && !data?.message) {
    return 'Проверьте введённые данные';
  }
  if (err?.response?.status === 503) {
    return typeof data?.message === 'string' ? data.message : 'Сервис временно недоступен';
  }
  if (err?.code === 'ERR_NETWORK') {
    if (process.env.NODE_ENV === 'development') {
      return 'Нет связи с сервером. Запущен ли backend (php artisan serve)?';
    }
    return 'Нет связи с сервером. Проверьте интернет и обновите страницу (Ctrl+F5). Если не помогло — напишите в IT.';
  }
  if (typeof err?.message === 'string' && err.message.trim()) {
    return err.message;
  }
  return fallback;
}

function parseJsonResponse(data) {
  if (data && typeof data === 'object') return data;
  if (typeof data !== 'string') return data;
  const arrStart = data.indexOf('[');
  const objStart = data.indexOf('{');
  const start = arrStart === -1 ? objStart : objStart === -1 ? arrStart : Math.min(arrStart, objStart);
  if (start === -1) return data;
  try {
    return JSON.parse(data.slice(start));
  } catch {
    return data;
  }
}

function normalizeSettingsResponse(data) {
  if (!data || typeof data !== 'object') return data;
  return {
    ...data,
    adminPercent: data.adminPercent ?? data.admin_percent,
    paymentQrUrl: data.paymentQrUrl ?? data.payment_qr_url,
    clinicName: data.clinicName ?? data.clinic_name,
    clinicInn: data.clinicInn ?? data.clinic_inn,
    clinicAddress: data.clinicAddress ?? data.clinic_address,
    symptomOptions: data.symptomOptions ?? data.symptom_options,
    symptomRules: data.symptomRules ?? data.symptom_rules,
    monthlyRevenueGoal: data.monthlyRevenueGoal ?? data.monthly_revenue_goal,
  };
}

function normalizeBooking(item) {
  if (!item) return item;
  return {
    ...item,
    id: item.id,
    doctorId: item.doctorId ?? item.masterId ?? item.doctor_id ?? item.master_id,
    masterId: item.masterId ?? item.doctorId ?? item.master_id ?? item.doctor_id,
    clientId: item.clientId ?? item.client_id,
    serviceId: item.serviceId ?? item.service_id,
    clientName: item.clientName ?? item.client_name ?? item.client?.full_name ?? '',
    clientPhone: item.clientPhone ?? item.client_phone ?? item.client?.phone ?? '',
    clientBirthDate:
      item.clientBirthDate ??
      item.client_birth_date ??
      item.client?.birthDate ??
      item.client?.birth_date ??
      '',
    serviceName: item.serviceName ?? item.service_name ?? item.service?.name ?? '',
    doctorName: item.doctorName ?? item.doctor_name ?? item.doctor?.name ?? '',
    roomId: item.roomId ?? item.room_id ?? null,
    roomName: item.roomName ?? item.room_name ?? item.room?.name ?? null,
    date: item.date,
    time: item.time,
    duration: item.duration,
    status: item.status,
    isPreliminary: !!(item.isPreliminary ?? item.is_preliminary),
    bookingSource: item.bookingSource ?? item.booking_source ?? null,
    queueNumber: item.queueNumber ?? item.queue_number ?? null,
    ticketCode: item.ticketCode ?? item.ticket_code ?? null,
    patientArrivedAt: item.patientArrivedAt ?? item.patient_arrived_at ?? null,
    patientArrived: !!(
      item.patientArrived ??
      item.patient_arrived ??
      item.patientArrivedAt ??
      item.patient_arrived_at
    ),
    diagnosis: item.diagnosis ?? null,
    diagnoses: Array.isArray(item.diagnoses) ? item.diagnoses : [],
    recommendations: item.recommendations ?? null,
    protocolExamination: item.protocolExamination ?? item.protocol_examination ?? null,
    protocolComplaints: item.protocolComplaints ?? item.protocol_complaints ?? null,
    protocolAnamnesisLife: item.protocolAnamnesisLife ?? item.protocol_anamnesis_life ?? null,
    protocolAnamnesisDisease: item.protocolAnamnesisDisease ?? item.protocol_anamnesis_disease ?? null,
    protocolObjectiveStatus: item.protocolObjectiveStatus ?? item.protocol_objective_status ?? null,
    protocolLocalStatus: item.protocolLocalStatus ?? item.protocol_local_status ?? null,
    protocolSpecialtyStatus:
      item.protocolSpecialtyStatus ?? item.protocol_specialty_status ?? null,
    protocolSpecialty: item.protocolSpecialty ?? item.protocol_specialty ?? null,
    protocolMeta: item.protocolMeta ?? item.protocol_meta ?? null,
    specialtyStatusLabel: item.specialtyStatusLabel ?? item.specialty_status_label ?? null,
    protocolPostExamComments:
      item.protocolPostExamComments ?? item.protocol_post_exam_comments ?? null,
    progressNote: item.progressNote ?? item.progress_note ?? null,
    severity: item.severity ?? null,
    visitNumber: item.visitNumber ?? item.visit_number ?? null,
    supplementarySequence: item.supplementarySequence ?? item.supplementary_sequence ?? null,
    surgerySequence: item.surgerySequence ?? item.surgery_sequence ?? null,
    surgeryRegisterNumber: item.surgeryRegisterNumber ?? item.surgery_register_number ?? null,
    surgeryMode: item.surgeryMode ?? item.surgery_mode ?? null,
    serviceKind: item.serviceKind ?? item.service_kind ?? null,
    needsPayment: !!(item.needsPayment ?? item.needs_payment),
    billableAmount:
      item.billableAmount ?? item.billable_amount ?? null,
    paidAmount: item.paidAmount ?? item.paid_amount ?? null,
    isPaymentVisit: !!(item.isPaymentVisit ?? item.is_payment_visit),
    dayOfStay: item.dayOfStay ?? item.day_of_stay ?? null,
    treatmentCourseId: item.treatmentCourseId ?? item.treatment_course_id ?? null,
    careType: item.careType ?? item.care_type ?? null,
    billingMode: item.billingMode ?? item.billing_mode ?? null,
    fromCashier: !!(item.fromCashier ?? item.from_cashier),
    treatmentCompleted: !!(item.treatmentCompleted ?? item.treatment_completed),
    medicalRecordClosedAt: item.medicalRecordClosedAt ?? item.medical_record_closed_at ?? null,
    closedByUserId: item.closedByUserId ?? item.closed_by_user_id ?? null,
    medicalConsultations: item.medicalConsultations ?? item.medical_consultations ?? [],
    medicalStudyItemIds:
      item.medicalStudyItemIds ??
      item.medical_study_item_ids ??
      (Array.isArray(item.medicalStudyItems)
        ? item.medicalStudyItems.map((s) => s.id)
        : []),
    medicalStudyItems: item.medicalStudyItems ?? item.medical_study_items ?? [],
  };
}

function normalizeService(item) {
  if (!item) return item;
  return {
    ...item,
    serviceKind: item.serviceKind ?? item.service_kind ?? 'standard',
    careType: item.careType ?? item.care_type ?? 'outpatient',
    billingMode: item.billingMode ?? item.billing_mode ?? 'per_visit',
    courseVisitsCount: item.courseVisitsCount ?? item.course_visits_count ?? null,
    paymentDueOnVisit: item.paymentDueOnVisit ?? item.payment_due_on_visit ?? null,
    coursePrice: item.coursePrice ?? item.course_price ?? null,
    paymentSchedule: item.paymentSchedule ?? item.payment_schedule ?? [],
    expenseAmount: item.expenseAmount ?? item.expense_amount ?? 0,
    expenseNote: item.expenseNote ?? item.expense_note ?? null,
    doctorProfitPercent: item.doctorProfitPercent ?? item.doctor_profit_percent ?? null,
    nurseShareAmount: item.nurseShareAmount ?? item.nurse_share_amount ?? null,
    defaultNurseId: item.defaultNurseId ?? item.default_nurse_id ?? null,
    defaultNurseName: item.defaultNurseName ?? item.default_nurse_name ?? null,
    cleanProfit: item.cleanProfit ?? item.clean_profit ?? null,
    doctorShareAmount: item.doctorShareAmount ?? item.doctor_share_amount ?? null,
    clinicShareAmount: item.clinicShareAmount ?? item.clinic_share_amount ?? null,
    categoryId: item.categoryId ?? item.category_id ?? null,
    categoryName: item.categoryName ?? item.category_name ?? item.category?.name ?? null,
    doctorIds: item.doctorIds ?? item.doctor_ids ?? (Array.isArray(item.doctors) ? item.doctors.map((d) => d.id) : []),
  };
}

function physioServicePayload(data) {
  return {
    name: data.name || 'Физкабинет',
    service_kind: 'physio',
    price: Number(data.price) || 0,
    duration: Number(data.duration) || 30,
    category_id: data.categoryId ?? data.category_id ?? null,
  };
}

function operationServicePayload(data) {
  return {
    name: data.name,
    service_kind: 'operation',
    care_type: data.careType ?? data.care_type ?? 'outpatient',
    price: Number(data.price) || 0,
    duration: Number(data.duration) || 60,
    doctor_profit_percent: Number(data.doctorProfitPercent ?? data.doctor_profit_percent ?? 50),
    category_id: data.categoryId ?? data.category_id ?? null,
  };
}

function supplementaryServicePayload(data) {
  return {
    name: data.name,
    service_kind: 'supplementary',
    care_type: data.careType ?? data.care_type ?? 'outpatient',
    price: Number(data.price),
    expense_amount: Number(data.expenseAmount ?? data.expense_amount ?? 0),
    expense_note: data.expenseNote ?? data.expense_note ?? null,
    doctor_profit_percent: Number(data.doctorProfitPercent ?? data.doctor_profit_percent ?? 0),
    nurse_share_amount:
      data.nurseShareAmount != null || data.nurse_share_amount != null
        ? Number(data.nurseShareAmount ?? data.nurse_share_amount)
        : null,
    doctor_share_amount:
      data.doctorShareAmount != null || data.doctor_share_amount != null
        ? Number(data.doctorShareAmount ?? data.doctor_share_amount)
        : null,
    default_nurse_id:
      data.defaultNurseId != null || data.default_nurse_id != null
        ? Number(data.defaultNurseId ?? data.default_nurse_id) || null
        : null,
    duration: Number(data.duration) || 15,
    category_id: data.categoryId ?? data.category_id ?? null,
  };
}

function servicePayload(data) {
  const careType = data.careType ?? data.care_type ?? 'outpatient';
  let billingMode = data.billingMode ?? data.billing_mode ?? 'per_visit';
  if (careType === 'inpatient') {
    billingMode = 'inpatient_daily';
  }

  const payload = {
    name: data.name,
    duration: Number(data.duration),
    price: Number(data.price),
    care_type: careType,
    billing_mode: billingMode,
    category_id: data.categoryId ?? data.category_id ?? null,
    course_visits_count: null,
    payment_due_on_visit: null,
    course_price: null,
    payment_schedule: null,
  };

  if (billingMode === 'course_deferred') {
    payload.course_visits_count = Number(data.courseVisitsCount ?? data.course_visits_count) || null;
    payload.payment_due_on_visit = Number(data.paymentDueOnVisit ?? data.payment_due_on_visit) || null;
    payload.course_price = Number(data.coursePrice ?? data.course_price ?? 0);
  } else if (billingMode === 'course_installments') {
    payload.course_visits_count = Number(data.courseVisitsCount ?? data.course_visits_count) || null;
    payload.payment_schedule = (data.paymentSchedule ?? data.payment_schedule ?? []).map((r) => ({
      visit: Number(r.visit),
      amount: Number(r.amount),
    }));
    payload.course_price = Number(data.coursePrice ?? data.course_price ?? 0);
  } else if (billingMode === 'course_split') {
    payload.course_visits_count = Number(data.courseVisitsCount ?? data.course_visits_count) || null;
    payload.course_price = Number(data.coursePrice ?? data.course_price ?? 0);
  } else if (billingMode === 'inpatient_daily') {
    payload.course_visits_count = Number(data.courseVisitsCount ?? data.course_visits_count) || null;
  }

  return payload;
}

export const treatmentCoursesApi = {
  list: (params) =>
    api
      .get('/treatment-courses', { params, headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  discharge: (id, payload = {}) => {
    const body =
      typeof payload === 'string'
        ? { date: payload }
        : {
            ...(payload?.date ? { date: payload.date } : {}),
            ...(payload?.billedDays ? { billed_days: payload.billedDays } : {}),
          };
    return api
      .post(`/treatment-courses/${id}/discharge`, body, { headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r));
  },
  dischargeReadiness: (id) =>
    api
      .get(`/treatment-courses/${id}/discharge-readiness`, { headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  inpatientBoard: (params) =>
    api
      .get('/treatment-courses', {
        params: { ...(params || {}), inpatient_board: 1 },
        headers: authHeaders(),
      })
      .then((r) => parseJsonResponse(r?.data ?? r))
      .catch((err) => {
        if (err?.response?.status !== 404) {
          throw err;
        }
        return api
          .get('/treatment-courses/inpatient-board', { params, headers: authHeaders() })
          .then((r) => parseJsonResponse(r?.data ?? r));
      }),
  updatePlannedDischarge: (id, plannedDischargedAt) =>
    api
      .patch(
        `/treatment-courses/${id}/planned-discharge`,
        { planned_discharged_at: plannedDischargedAt || null },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
  updateAdmission: (id, admittedAt) =>
    api
      .patch(
        `/treatment-courses/${id}/admission`,
        { admitted_at: admittedAt },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
  updateBilledDays: (id, billedDays) =>
    api
      .patch(
        `/treatment-courses/${id}/billed-days`,
        { billed_days: billedDays },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
  updateTreatingDoctor: (id, doctorId, { syncMedicalCard = true } = {}) =>
    api
      .patch(
        `/treatment-courses/${id}/treating-doctor`,
        { treating_doctor_id: doctorId, sync_medical_card: syncMedicalCard },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
  updateRoom: (id, roomId) =>
    api
      .patch(
        `/treatment-courses/${id}/room`,
        { room_id: roomId },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
  transferRoom: (id, roomId, transferDate) =>
    api
      .post(
        `/treatment-courses/${id}/transfer-room`,
        { room_id: roomId, transfer_date: transferDate },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
  transferRoomPreview: (id, roomId, transferDate) =>
    api
      .get(`/treatment-courses/${id}/transfer-room-preview`, {
        params: { room_id: roomId, transfer_date: transferDate },
        headers: authHeaders(),
      })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  cancel: (id) =>
    api
      .delete(`/treatment-courses/${id}`, { headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  refundPreview: (id, params = {}) =>
    api
      .get(`/treatment-courses/${id}/refund-preview`, {
        params: {
          ...(params.billedDays != null ? { billed_days: params.billedDays } : {}),
        },
        headers: authHeaders(),
      })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  refundUnusedDays: (id, payload = {}) =>
    api
      .post(
        `/treatment-courses/${id}/refund-unused-days`,
        {
          ...(payload.amount != null ? { amount: payload.amount } : {}),
          ...(payload.billedDays != null ? { billed_days: payload.billedDays } : {}),
          ...(payload.reason ? { reason: payload.reason } : {}),
          ...(payload.discharge != null ? { discharge: payload.discharge } : {}),
        },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
};

export const authApi = {
  login: (email, password) =>
    api.post('/login', { email, password }).then((r) => {
      const raw = r?.data ?? r;
      const data = parseJsonResponse(raw);
      debug('authApi.login response', { wasString: typeof raw === 'string', hasToken: !!data?.token });
      return data;
    }),
  logout: () => api.post('/logout', {}, { headers: authHeaders() }).then((r) => r.data),
  user: () => api.get('/user', { headers: authHeaders() }).then((r) => r.data),
  updateAccountingViewMode: (mode) =>
    api
      .put('/users/me/accounting-view-mode', { mode }, { headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  updateActiveLedger: (ledger) =>
    api
      .put('/users/me/active-ledger', { ledger }, { headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
};

export const ledgerMirrorApi = {
  status: () =>
    api.get('/ledger-mirror/status', { headers: authHeaders() }).then((r) => parseJsonResponse(r?.data ?? r)),
};

export const clientsApi = {
  checkPhone: (phone, excludeId) =>
    api
      .get('/clients/check-phone', {
        params: { phone, ...(excludeId ? { exclude_id: excludeId } : {}) },
        headers: authHeaders(),
      })
      .then((r) => r.data),
  list: (params) => {
    const query =
      typeof params === 'string'
        ? { search: params }
        : { ...(params || {}) };
    return api
      .get('/clients', { params: query, headers: authHeaders() })
      .then((r) => {
        const body = parseJsonResponse(r?.data ?? r);
        if (body && Array.isArray(body.data)) {
          return body;
        }
        if (Array.isArray(body)) {
          return {
            data: body,
            meta: {
              total: body.length,
              page: 1,
              per_page: body.length,
              last_page: 1,
            },
          };
        }
        return {
          data: [],
          meta: { total: 0, page: 1, per_page: 50, last_page: 1 },
        };
      });
  },
  get: (id) =>
    api.get(`/clients/${id}`, { headers: authHeaders() }).then((r) => parseJsonResponse(r?.data ?? r)),
  updateTaxReport: (id, included) =>
    api
      .patch(
        `/clients/${id}/tax-report`,
        { included: Boolean(included) },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
  create: (data) => {
    const payload = {
      full_name: data.fullName,
      phone: data.phone,
      phone_raw: data.phoneRaw || data.phone?.replace(/\D/g, ''),
      birth_date: data.birthDate || null,
      age: data.age ?? null,
      iin: data.iin || null,
      preferred_doctor_id: data.preferredDoctorId || data.preferredMasterId || null,
      source: data.source || null,
      main_diagnosis: data.mainDiagnosis || null,
      allergies: data.allergies || null,
      chronic: data.chronic || null,
      status: data.status || 'active',
      segment: data.segment || 'regular',
      registration_type: data.registrationType || 'ambulatory',
      address: data.address || null,
      inpatient_registration_data: data.inpatientRegistrationData || null,
      comments: data.comments || '',
      course_visit_reminder_start: data.courseVisitReminderStart || null,
      force_new: Boolean(data.forceNew),
      existing_client_id: data.existingClientId || null,
    };
    return api
      .post('/clients', payload, { headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r));
  },
  updateBirthDate: (id, birthDate) =>
    api
      .patch(`/clients/${id}`, { birthDate }, { headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  update: (id, data) => {
    const payload = {
      full_name: data.fullName,
      phone: data.phone,
      phone_raw: data.phoneRaw || data.phone?.replace(/\D/g, ''),
      birth_date: data.birthDate || null,
      age: data.age ?? null,
      iin: data.iin ?? null,
      preferred_doctor_id: data.preferredDoctorId || data.preferredMasterId || null,
      source: data.source || null,
      main_diagnosis: data.mainDiagnosis || null,
      allergies: data.allergies || null,
      chronic: data.chronic || null,
      status: data.status,
      segment: data.segment,
      registration_type: data.registrationType,
      address: data.address ?? null,
      inpatient_registration_data: data.inpatientRegistrationData ?? null,
      comments: data.comments || '',
      course_visit_reminder_start: data.courseVisitReminderStart ?? null,
    };
    if (data.medicalCard003u != null) {
      payload.medical_card_003u = data.medicalCard003u;
    }
    return api
      .put(`/clients/${id}`, payload, { headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r));
  },
  delete: (id) => api.delete(`/clients/${id}`, { headers: authHeaders() }),
  uploadPhoto: (id, file) => {
    const form = new FormData();
    form.append('photo', file);
    return api
      .post(`/clients/${id}/photo`, form, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => parseJsonResponse(r?.data ?? r));
  },
  deletePhoto: (id) =>
    api
      .delete(`/clients/${id}/photo`, { headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  admitInpatient: (clientId, data) =>
    api
      .post(
        `/clients/${clientId}/inpatient-admission`,
        {
          service_id: data.serviceId,
          source_booking_id: data.sourceBookingId || null,
          admitted_at: data.admittedAt || null,
          planned_discharged_at: data.plannedDischargedAt || null,
          referral_notes: data.referralNotes || null,
          copy_diagnosis_to_client: !!data.copyDiagnosisToClient,
          treating_doctor_id: data.treatingDoctorId || null,
          room_id: data.roomId,
          icd10_code: data.icd10Code || null,
          icd10_name: data.icd10Name || null,
          icd10_diagnosis_type: data.icd10DiagnosisType || null,
        },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
};

export const clientCriticalSituationsApi = {
  list: (clientId) =>
    api
      .get(`/clients/${clientId}/critical-situations`, { headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  start: (clientId, title) =>
    api
      .post(
        `/clients/${clientId}/critical-situations`,
        title ? { title } : {},
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
  close: (clientId, situationId, summary) =>
    api
      .post(
        `/clients/${clientId}/critical-situations/${situationId}/close`,
        summary ? { summary } : {},
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
  addEntry: (clientId, situationId, { content, eventAt, source }) =>
    api
      .post(
        `/clients/${clientId}/critical-situations/${situationId}/entries`,
        {
          content,
          event_at: eventAt,
          source: source || 'manual',
        },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
  updateEntry: (clientId, situationId, entryId, { content, eventAt }) =>
    api
      .put(
        `/clients/${clientId}/critical-situations/${situationId}/entries/${entryId}`,
        {
          ...(content !== undefined ? { content } : {}),
          ...(eventAt !== undefined ? { event_at: eventAt } : {}),
        },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
  deleteEntry: (clientId, situationId, entryId) =>
    api.delete(`/clients/${clientId}/critical-situations/${situationId}/entries/${entryId}`, {
      headers: authHeaders(),
    }),
};

export const protocolArchiveApi = {
  list: (params) =>
    api
      .get('/protocol-archive', { params: params || {}, headers: authHeaders() })
      .then((r) => {
        const body = parseJsonResponse(r?.data ?? r);
        if (body && Array.isArray(body.data)) {
          return body;
        }
        return {
          data: [],
          meta: { total: 0, page: 1, per_page: 30, last_page: 1 },
        };
      }),
  get: (clientId) =>
    api
      .get(`/protocol-archive/${clientId}`, { headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
};

export const inpatientConsultCallsApi = {
  list: (params = {}) =>
    api
      .get('/inpatient-consult-calls', {
        params: {
          client_id: params.clientId ?? params.client_id,
          treatment_course_id: params.treatmentCourseId ?? params.treatment_course_id,
        },
        headers: authHeaders(),
      })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  create: (data) =>
    api
      .post(
        '/inpatient-consult-calls',
        {
          treatment_course_id: data.treatmentCourseId ?? data.treatment_course_id,
          specialist_doctor_ids: data.specialistDoctorIds ?? data.specialist_doctor_ids,
          note: data.note ?? null,
        },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
  writeResult: (id, data) =>
    api
      .post(
        `/inpatient-consult-calls/${id}/result`,
        {
          mode: data.mode,
          diagnosis: data.diagnosis ?? '',
          recommendations: data.recommendations ?? '',
          complaints: data.complaints ?? '',
          status: data.status ?? '',
          orders: data.orders ?? '',
          text: data.text ?? '',
        },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
};

export const clientMedicalRecordGrantsApi = {
  list: (clientId) =>
    api
      .get(`/clients/${clientId}/medical-record-grants`, { headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  create: (clientId, data) =>
    api
      .post(`/clients/${clientId}/medical-record-grants`, {
        granted_to_doctor_id: data.grantedToDoctorId ?? data.granted_to_doctor_id,
        duration_hours: data.durationHours ?? data.duration_hours,
        can_edit: data.canEdit ?? data.can_edit ?? true,
        note: data.note ?? null,
      }, { headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  revoke: (clientId, grantId) =>
    api
      .delete(`/clients/${clientId}/medical-record-grants/${grantId}`, { headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
};

export const sirocaLisApi = {
  status: () =>
    api.get('/lis/status', { headers: authHeaders() }).then((r) => parseJsonResponse(r?.data ?? r)),
  serviceCodes: () =>
    api
      .get('/lis/service-codes', { headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  syncClient: (clientId) =>
    api
      .post(`/clients/${clientId}/lis/sync`, {}, { headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  sendBooking: (bookingId, serviceCodes = []) =>
    api
      .post(
        `/bookings/${bookingId}/lis/send`,
        { service_codes: serviceCodes },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
};

export const clientLabDocumentsApi = {
  list: (clientId) =>
    api
      .get(`/clients/${clientId}/lab-documents`, { headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  upload: (clientId, file, title) => {
    const form = new FormData();
    form.append('file', file);
    if (title) form.append('title', title);
    return api
      .post(`/clients/${clientId}/lab-documents`, form, {
        headers: authHeaders(),
      })
      .then((r) => parseJsonResponse(r?.data ?? r));
  },
  delete: (clientId, docId) =>
    api.delete(`/clients/${clientId}/lab-documents/${docId}`, { headers: authHeaders() }),
  downloadBlob: (clientId, docId, doc = {}) =>
    api
      .get(`/clients/${clientId}/lab-documents/${docId}/download`, {
        params: { inline: 1 },
        headers: authHeaders(),
        responseType: 'blob',
      })
      .then((r) => parseAttachmentDownloadResponse(r, { ...doc, id: docId })),
  fetchBlob: (clientId, doc) =>
    fetchAttachmentBlob(clientLabDocumentsApi.downloadBlob, clientId, doc),
  open: (clientId, docOrId) => {
    const doc =
      docOrId && typeof docOrId === 'object'
        ? docOrId
        : { id: docOrId };
    return clientLabDocumentsApi.fetchBlob(clientId, doc).then((blob) => {
      openAttachmentBlob(blob, doc);
    });
  },
};

export const clientImagingDocumentsApi = {
  list: (clientId) =>
    api
      .get(`/clients/${clientId}/imaging-documents`, { headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  upload: (clientId, file, { studyType, title, notes } = {}) => {
    const form = new FormData();
    form.append('file', file);
    form.append('study_type', studyType || 'ultrasound');
    if (title) form.append('title', title);
    if (notes) form.append('notes', notes);
    return api
      .post(`/clients/${clientId}/imaging-documents`, form, {
        headers: authHeaders(),
      })
      .then((r) => parseJsonResponse(r?.data ?? r));
  },
  delete: (clientId, docId) =>
    api.delete(`/clients/${clientId}/imaging-documents/${docId}`, { headers: authHeaders() }),
  downloadBlob: (clientId, docId, doc = {}) =>
    api
      .get(`/clients/${clientId}/imaging-documents/${docId}/download`, {
        params: { inline: 1 },
        headers: authHeaders(),
        responseType: 'blob',
      })
      .then((r) => parseAttachmentDownloadResponse(r, { ...doc, id: docId })),
  fetchBlob: (clientId, doc) =>
    fetchAttachmentBlob(clientImagingDocumentsApi.downloadBlob, clientId, doc),
  open: (clientId, docOrId) => {
    const doc = docOrId && typeof docOrId === 'object' ? docOrId : { id: docOrId };
    return clientImagingDocumentsApi.fetchBlob(clientId, doc).then((blob) => {
      openAttachmentBlob(blob, doc);
    });
  },
};

export const mastersApi = {
  list: (params) =>
    api
      .get('/doctors', { params: params || {}, headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  create: (data) => {
    const employeeType = data.employeeType ?? data.employee_type ?? 'doctor';
    const createUser = data.createUser === true;
    const isStaffOnly = ['staff', 'reception', 'nurse'].includes(employeeType);

    return api
      .post(
        '/doctors',
        {
          name: data.name,
          percent: data.percent ?? (isStaffOnly ? 0 : 50),
          employee_type: employeeType,
          payroll_scheme:
            data.payrollScheme ?? data.payroll_scheme ?? (isStaffOnly ? 'fixed_salary' : 'ambulatory'),
          monthly_salary: data.monthlySalary ?? data.monthly_salary ?? null,
          shift_rate: data.shiftRate ?? data.shift_rate ?? null,
          night_shift_rate: data.nightShiftRate ?? data.night_shift_rate ?? null,
          inpatient_pool_multiplier: data.inpatientPoolMultiplier ?? data.inpatient_pool_multiplier ?? null,
          inpatient_pool_divisor: data.inpatientPoolDivisor ?? data.inpatient_pool_divisor ?? null,
          salary_effective_from: data.salaryEffectiveFrom ?? data.salary_effective_from ?? null,
          phone: data.phone || null,
          nfc_card_uid: data.nfcCardUid ?? data.nfc_card_uid ?? null,
          room_id: data.roomId ?? data.room_id ?? null,
          service_ids: data.serviceIds ?? data.service_ids ?? [],
          create_user: createUser,
          ...(createUser
            ? {
                user: {
                  name: data.userName || data.name,
                  email: data.email,
                  password: data.password,
                },
              }
            : {}),
        },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r));
  },
  update: (id, data) =>
    api
      .put(
        `/doctors/${id}`,
        {
          name: data.name,
          percent: data.percent ?? 50,
          employee_type: data.employeeType ?? data.employee_type,
          payroll_scheme: data.payrollScheme ?? data.payroll_scheme,
          monthly_salary: data.monthlySalary ?? data.monthly_salary ?? null,
          shift_rate: data.shiftRate ?? data.shift_rate ?? null,
          night_shift_rate: data.nightShiftRate ?? data.night_shift_rate ?? null,
          inpatient_pool_multiplier: data.inpatientPoolMultiplier ?? data.inpatient_pool_multiplier ?? null,
          inpatient_pool_divisor: data.inpatientPoolDivisor ?? data.inpatient_pool_divisor ?? null,
          salary_effective_from: data.salaryEffectiveFrom ?? data.salary_effective_from ?? null,
          phone: data.phone ?? null,
          nfc_card_uid: data.nfcCardUid ?? data.nfc_card_uid ?? null,
          room_id: data.roomId ?? data.room_id ?? null,
          service_ids: data.serviceIds ?? data.service_ids ?? [],
          user: data.user
            ? {
                name: data.userName,
                email: data.email,
                password: data.password || undefined,
              }
            : undefined,
        },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
  get: (id) =>
    api.get(`/doctors/${id}`, { headers: authHeaders() }).then((r) => parseJsonResponse(r?.data ?? r)),
  delete: (id) => api.delete(`/doctors/${id}`, { headers: authHeaders() }),
};

export const roomsApi = {
  list: (params) =>
    api
      .get('/rooms', { params, headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  occupancy: (date) =>
    api
      .get('/rooms/occupancy', {
        params: date ? { date } : undefined,
        headers: authHeaders(),
      })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  create: (data) =>
    api
      .post(
        '/rooms',
        {
        name: data.name,
          sort_order: data.sortOrder ?? data.sort_order ?? 0,
          bed_capacity: data.bedCapacity ?? data.bed_capacity ?? 1,
          floor: data.floor ?? null,
          room_type: data.roomType ?? data.room_type ?? 'standard',
          daily_rate: data.dailyRate ?? data.daily_rate ?? null,
        },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
  createBulk: (data) =>
    api
      .post(
        '/rooms/bulk',
        {
          floor: data.floor,
          from: data.from,
          to: data.to,
          prefix: data.prefix ?? '',
          suffix: data.suffix ?? '',
          pad_length: data.padLength ?? data.pad_length ?? 0,
          bed_capacity: data.bedCapacity ?? data.bed_capacity ?? 1,
          sort_order_start: data.sortOrderStart ?? data.sort_order_start ?? 0,
          room_type: data.roomType ?? data.room_type ?? 'standard',
          daily_rate: data.dailyRate ?? data.daily_rate ?? null,
        },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
  update: (id, data) =>
    api
      .put(
        `/rooms/${id}`,
        {
        name: data.name,
          sort_order: data.sortOrder ?? data.sort_order ?? 0,
          bed_capacity: data.bedCapacity ?? data.bed_capacity ?? 1,
          floor: data.floor ?? null,
          room_type: data.roomType ?? data.room_type ?? 'standard',
          daily_rate: data.dailyRate ?? data.daily_rate ?? null,
        },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
  delete: (id) => api.delete(`/rooms/${id}`, { headers: authHeaders() }),
};

export const attendanceApi = {
  list: (params) =>
    api
      .get('/attendance', { params, headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  summary: () =>
    api
      .get('/attendance/summary', { headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  scanNfc: (uid) =>
    api
      .post('/attendance/nfc', { uid }, { headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  dismissLateAlert: (attendanceLogId) =>
    api
      .post(`/attendance/${attendanceLogId}/dismiss-late`, {}, { headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  assignCard: (doctorId, uid) =>
    api
      .post(`/doctors/${doctorId}/nfc-card`, { uid }, { headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  delete: (id) => api.delete(`/attendance/${id}`, { headers: authHeaders() }),
};

export const receptionLeadsApi = {
  funnel: (params) =>
    api
      .get('/reception-leads/funnel', { params, headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  markLost: (clientId) =>
    api
      .post(`/reception-leads/clients/${clientId}/lost`, {}, { headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
};

export const payrollApi = {
  receptionBonuses: (params) =>
    api
      .get('/payroll/reception-bonuses', { params, headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  report: (params) =>
    api.get('/payroll/report', { params, headers: authHeaders() }).then((r) => parseJsonResponse(r?.data ?? r)),
  summaryAll: (params) =>
    api.get('/payroll/summary-all', { params, headers: authHeaders() }).then((r) => parseJsonResponse(r?.data ?? r)),
  doctors: () =>
    api.get('/payroll/doctors', { headers: authHeaders() }).then((r) => parseJsonResponse(r?.data ?? r)),
  getSettings: () =>
    api.get('/payroll/settings', { headers: authHeaders() }).then((r) => parseJsonResponse(r?.data ?? r)),
  updateSettings: (data) =>
    api.put('/payroll/settings', data, { headers: authHeaders() }).then((r) => parseJsonResponse(r?.data ?? r)),
  advanceReminders: (params) =>
    api
      .get('/payroll/advance-reminders', { params: params || {}, headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  advances: (params) =>
    api.get('/payroll/advances', { params, headers: authHeaders() }).then((r) => parseJsonResponse(r?.data ?? r)),
  createAdvance: (data) =>
    api
      .post(
        '/payroll/advances',
        {
          doctor_id: data.doctorId,
          amount: Number(data.amount),
          paid_at: data.paidAt || null,
          earnings_period_from: data.earningsPeriodFrom || null,
          earnings_period_to: data.earningsPeriodTo || null,
          deduct_month: data.deductMonth || null,
          accrued_at_time: data.accruedAtTime != null ? Number(data.accruedAtTime) : null,
          note: data.note || null,
        },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
  updateAdvance: (id, data) =>
    api
      .patch(
        `/payroll/advances/${id}`,
        { deduct_month: data.deductMonth },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
  markAdvanceDeducted: (id) =>
    api
      .post(`/payroll/advances/${id}/mark-deducted`, {}, { headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  cancelAdvance: (id) =>
    api.delete(`/payroll/advances/${id}`, { headers: authHeaders() }).then((r) => parseJsonResponse(r?.data ?? r)),
  upsertOfficialCard: (data) =>
    api
      .put(
        '/payroll/official-card',
        {
          doctor_id: data.doctorId,
          date_from: data.dateFrom,
          date_to: data.dateTo,
          amount: Number(data.amount) || 0,
          note: data.note || null,
        },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
  upsertOfficialCardBatch: (data) =>
    api
      .put(
        '/payroll/official-card-batch',
        {
          date_from: data.dateFrom,
          date_to: data.dateTo,
          rows: (data.rows || []).map((row) => ({
            doctor_id: row.doctorId,
            amount: Number(row.amount) || 0,
            note: row.note || null,
          })),
        },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
  setPaidMark: (data) =>
    api
      .put(
        '/payroll/paid-mark',
        {
          doctor_id: data.doctorId,
          date_from: data.dateFrom,
          date_to: data.dateTo,
          paid: !!data.paid,
        },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
  setShiftCount: (data) =>
    api
      .put(
        '/payroll/shift-count',
        {
          doctor_id: data.doctorId,
          date_from: data.dateFrom,
          date_to: data.dateTo,
          shift_count: data.shiftCount === '' || data.shiftCount == null ? null : Number(data.shiftCount),
          ...(data.nightShiftCount !== undefined
            ? {
                night_shift_count:
                  data.nightShiftCount === '' || data.nightShiftCount == null
                    ? null
                    : Number(data.nightShiftCount),
              }
            : {}),
        },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
  setTaxAmount: (data) =>
    api
      .put(
        '/payroll/tax-amount',
        {
          doctor_id: data.doctorId,
          date_from: data.dateFrom,
          date_to: data.dateTo,
          tax_amount: data.taxAmount === '' || data.taxAmount == null ? null : Number(data.taxAmount),
          sync_default: data.syncDefault !== false,
        },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
  updateOperationExpenses: (data) =>
    api
      .put(
        '/payroll/operation-expenses',
        {
          staff_payment_id: data.staffPaymentId,
          doctor_id: data.doctorId,
          date_from: data.dateFrom,
          date_to: data.dateTo,
          ...buildOperationExpensePayload(data.expenses || {}),
        },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
  setEndoscopyCabinetExpenses: (data) =>
    api
      .put(
        '/payroll/endoscopy-cabinet-expenses',
        {
          doctor_id: data.doctorId,
          date_from: data.dateFrom,
          date_to: data.dateTo,
          amount: data.amount === '' || data.amount == null ? null : Number(data.amount),
          note: data.note === undefined ? undefined : data.note === '' ? null : String(data.note),
        },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
  paidLeaveStaffOptions: () =>
    api.get('/payroll/paid-leaves/staff-options', { headers: authHeaders() }).then((r) => parseJsonResponse(r?.data ?? r)),
  paidLeaves: (params) =>
    api
      .get('/payroll/paid-leaves', { params, headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  createPaidLeave: (data) =>
    api
      .post(
        '/payroll/paid-leaves',
        {
          doctor_id: data.doctorId,
          substitute_doctor_id: data.substituteDoctorId || null,
          date_from: data.dateFrom,
          date_to: data.dateTo,
          is_paid: data.isPaid !== false,
          note: data.note || null,
          substitute_payout_override:
            data.substitutePayoutOverride !== undefined && data.substitutePayoutOverride !== '' && data.substitutePayoutOverride !== null
              ? Number(data.substitutePayoutOverride)
              : null,
        },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
  deletePaidLeave: (id) =>
    api.delete(`/payroll/paid-leaves/${id}`, { headers: authHeaders() }).then((r) => parseJsonResponse(r?.data ?? r)),
  setMonthlySalary: (data) =>
    api
      .put(
        '/payroll/monthly-salary',
        {
          doctor_id: data.doctorId,
          date_from: data.dateFrom,
          date_to: data.dateTo,
          monthly_salary: Number(data.monthlySalary),
          effective_from: data.effectiveFrom || null,
        },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
  setNightDutyCount: (data) =>
    api
      .put(
        '/payroll/night-duty-count',
        {
          doctor_id: data.doctorId,
          date_from: data.dateFrom,
          date_to: data.dateTo,
          night_duty_count:
            data.nightDutyCount === '' || data.nightDutyCount == null
              ? null
              : Number(data.nightDutyCount),
        },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
};

export const staffPenaltiesApi = {
  list: (params) =>
    api.get('/staff-penalties', { params, headers: authHeaders() }).then((r) => parseJsonResponse(r?.data ?? r)),
  create: (data) =>
    api.post('/staff-penalties', {
      doctor_id: data.doctorId,
      amount: Number(data.amount),
      reason: data.reason || null,
      preset_key: data.presetKey || null,
      date: data.date,
      attendance_log_id: data.attendanceLogId || null,
    }, { headers: authHeaders() }).then((r) => parseJsonResponse(r?.data ?? r)),
  delete: (id) => api.delete(`/staff-penalties/${id}`, { headers: authHeaders() }),
};

export const serviceCategoriesApi = {
  list: () =>
    api
      .get('/service-categories', { headers: authHeaders() })
      .then((r) => {
        const data = parseJsonResponse(r?.data ?? r);
        return Array.isArray(data) ? data : [];
      }),
  create: (data) =>
    api
      .post(
        '/service-categories',
        {
          name: data.name,
          sort_order: data.sortOrder ?? data.sort_order ?? null,
        },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
  update: (id, data) =>
    api
      .put(
        `/service-categories/${id}`,
        {
          ...(data.name != null ? { name: data.name } : {}),
          ...(data.sortOrder != null || data.sort_order != null
            ? { sort_order: data.sortOrder ?? data.sort_order }
            : {}),
        },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
  delete: (id) => api.delete(`/service-categories/${id}`, { headers: authHeaders() }),
};

export const servicesApi = {
  list: (params) =>
    api
      .get('/services', { params, headers: authHeaders() })
      .then((r) => {
        const data = parseJsonResponse(r?.data ?? r);
        return (Array.isArray(data) ? data : []).map(normalizeService);
      }),
  createSupplementary: (data) =>
    api
      .post('/services', supplementaryServicePayload(data), { headers: authHeaders() })
      .then((r) => normalizeService(parseJsonResponse(r?.data ?? r))),
  updateSupplementary: (id, data) =>
    api
      .put(`/services/${id}`, supplementaryServicePayload(data), { headers: authHeaders() })
      .then((r) => normalizeService(parseJsonResponse(r?.data ?? r))),
  createOperation: (data) =>
    api
      .post('/services', operationServicePayload(data), { headers: authHeaders() })
      .then((r) => normalizeService(parseJsonResponse(r?.data ?? r))),
  updateOperation: (id, data) =>
    api
      .put(`/services/${id}`, operationServicePayload(data), { headers: authHeaders() })
      .then((r) => normalizeService(parseJsonResponse(r?.data ?? r))),
  createPhysio: (data) =>
    api
      .post('/services', physioServicePayload(data), { headers: authHeaders() })
      .then((r) => normalizeService(parseJsonResponse(r?.data ?? r))),
  updatePhysio: (id, data) =>
    api
      .put(`/services/${id}`, physioServicePayload(data), { headers: authHeaders() })
      .then((r) => normalizeService(parseJsonResponse(r?.data ?? r))),
  create: (data) =>
    api
      .post('/services', servicePayload(data), { headers: authHeaders() })
      .then((r) => normalizeService(parseJsonResponse(r?.data ?? r))),
  update: (id, data) =>
    api
      .put(`/services/${id}`, servicePayload(data), { headers: authHeaders() })
      .then((r) => normalizeService(parseJsonResponse(r?.data ?? r))),
  delete: (id) => api.delete(`/services/${id}`, { headers: authHeaders() }),
  uploadImage: (id, file) => {
    const form = new FormData();
    form.append('image', file);
    return api
      .post(`/services/${id}/image`, form, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => parseJsonResponse(r?.data ?? r));
  },
  deleteImage: (id) =>
    api
      .delete(`/services/${id}/image`, { headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
};

export const bookingsApi = {
  supplementaryBoard: (params) =>
    api
      .get('/bookings/supplementary-board', { params, headers: authHeaders() })
      .then((r) => {
        const data = parseJsonResponse(r?.data ?? r);
        const items = Array.isArray(data?.items) ? data.items : [];
        return {
          date: data?.date ?? null,
          summary: data?.summary ?? null,
          items: items.map(normalizeBooking),
        };
      }),
  surgeryBoard: (params) =>
    api
      .get('/bookings/surgery-board', { params, headers: authHeaders() })
      .then((r) => {
        const data = parseJsonResponse(r?.data ?? r);
        const items = Array.isArray(data?.items) ? data.items : [];
        return {
          date: data?.date ?? null,
          summary: data?.summary ?? null,
          items: items.map(normalizeBooking),
        };
      }),
  supplementaryReport: (params) =>
    api
      .get('/bookings/supplementary-report', { params, headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  createSupplementary: (data) =>
    api
      .post(
        '/bookings/supplementary',
        {
          ...(data.doctorId || data.masterId
            ? { doctor_id: data.doctorId || data.masterId }
            : {}),
          client_id: data.clientId || undefined,
          client_name: data.clientName?.trim() || undefined,
          service_id: data.serviceId,
          date: data.date || undefined,
        },
        { headers: authHeaders() }
      )
      .then((r) => normalizeBooking(parseJsonResponse(r?.data ?? r))),
  createSurgery: (data) =>
    api
      .post(
        '/bookings/surgery',
        {
          client_id: data.clientId,
          doctor_id: data.doctorId || data.masterId,
          service_id: data.serviceId,
          surgery_mode: data.surgeryMode,
          date: data.date || undefined,
          room_id: data.roomId ?? undefined,
        },
        { headers: authHeaders() }
      )
      .then((r) => normalizeBooking(parseJsonResponse(r?.data ?? r))),
  upcoming: (params) =>
    api
      .get('/bookings/upcoming', { params, headers: authHeaders() })
      .then((r) => {
        const data = parseJsonResponse(r?.data ?? r);
        return (Array.isArray(data) ? data : []).map(normalizeBooking);
      }),
  weekAccess: () =>
    api
      .get('/bookings/week-access', { headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  get: (id) =>
    api
      .get(`/bookings/${id}`, { headers: authHeaders() })
      .then((r) => normalizeBooking(parseJsonResponse(r?.data ?? r))),
  payments: (id) =>
    api
      .get(`/bookings/${id}/payments`, { headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  list: (params) =>
    api
      .get('/bookings', { params, headers: authHeaders() })
      .then((r) => {
        const data = parseJsonResponse(r?.data ?? r);
        return (Array.isArray(data) ? data : []).map(normalizeBooking);
      }),
  create: (data) =>
    api
      .post('/bookings', {
        doctor_id: data.doctorId || data.masterId,
        client_id: data.clientId,
        service_id: data.serviceId,
        room_id: data.roomId ?? data.room_id ?? null,
        date: data.date,
        time: data.time,
        duration: Number(data.duration),
        status: data.status || 'booked',
        is_preliminary: !!(data.isPreliminary ?? data.is_preliminary),
        ...(data.source ? { source: data.source } : {}),
      }, { headers: authHeaders() })
      .then((r) => normalizeBooking(parseJsonResponse(r?.data ?? r))),
  update: (id, data) => {
    const payload = {};
    if (data.doctorId != null || data.masterId != null) {
      payload.doctor_id = data.doctorId || data.masterId;
    }
    if (data.clientId != null) payload.client_id = data.clientId;
    if (data.serviceId != null) payload.service_id = data.serviceId;
    if (data.roomId !== undefined || data.room_id !== undefined) {
      payload.room_id = data.roomId ?? data.room_id ?? null;
    }
    if (data.date != null) payload.date = data.date;
    if (data.time != null) payload.time = data.time;
    if (data.duration != null) payload.duration = data.duration;
    if (data.status != null) payload.status = data.status;
    if (data.diagnosis !== undefined) payload.diagnosis = data.diagnosis || null;
    if (data.diagnoses !== undefined) {
      payload.diagnoses = Array.isArray(data.diagnoses) ? data.diagnoses : [];
    }
    if (data.recommendations !== undefined) payload.recommendations = data.recommendations || null;
    if (data.protocolExamination !== undefined) {
      payload.protocol_examination = data.protocolExamination || null;
    }
    if (data.protocolComplaints !== undefined) payload.protocol_complaints = data.protocolComplaints || null;
    if (data.protocolAnamnesisLife !== undefined) {
      payload.protocol_anamnesis_life = data.protocolAnamnesisLife || null;
    }
    if (data.protocolAnamnesisDisease !== undefined) {
      payload.protocol_anamnesis_disease = data.protocolAnamnesisDisease || null;
    }
    if (data.protocolObjectiveStatus !== undefined) {
      payload.protocol_objective_status = data.protocolObjectiveStatus || null;
    }
    if (data.protocolLocalStatus !== undefined) payload.protocol_local_status = data.protocolLocalStatus || null;
    if (data.protocolSpecialtyStatus !== undefined) {
      payload.protocol_specialty_status = data.protocolSpecialtyStatus || null;
    }
    if (data.protocolSpecialty !== undefined) payload.protocol_specialty = data.protocolSpecialty || null;
    if (data.protocolMeta !== undefined) {
      payload.protocol_meta =
        data.protocolMeta && typeof data.protocolMeta === 'object' ? data.protocolMeta : null;
    }
    if (data.protocolPostExamComments !== undefined) {
      payload.protocol_post_exam_comments = data.protocolPostExamComments || null;
    }
    if (data.progressNote !== undefined) payload.progress_note = data.progressNote || null;
    if (data.severity !== undefined) payload.severity = data.severity || null;
    if (data.treatmentCompleted !== undefined) {
      payload.treatment_completed = !!data.treatmentCompleted;
    }

    return api
      .put(`/bookings/${id}`, payload, { headers: authHeaders() })
      .then((r) => normalizeBooking(parseJsonResponse(r?.data ?? r)));
  },
  addConsultation: (id, data) =>
    api
      .post(`/bookings/${id}/consultations`, { text: data.text }, { headers: authHeaders() })
      .then((r) => normalizeBooking(parseJsonResponse(r?.data ?? r))),
  delete: (id) => api.delete(`/bookings/${id}`, { headers: authHeaders() }),
  call: (id) =>
    api
      .post(`/bookings/${id}/call`, {}, { headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  arrived: (id) =>
    api
      .post(`/bookings/${id}/arrived`, {}, { headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  queueTicket: (id) =>
    api
      .get(`/bookings/${id}/queue-ticket`, { headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
};

export const expensesApi = {
  categories: () => api.get('/expense-categories', { headers: authHeaders() }).then((r) => r.data),
  createCategory: (data) =>
    api
      .post('/expense-categories', { name: data.name }, { headers: authHeaders() })
      .then((r) => r.data),
  updateCategory: (id, data) =>
    api
      .put(`/expense-categories/${id}`, { name: data.name }, { headers: authHeaders() })
      .then((r) => r.data),
  deleteCategory: (id) =>
    api.delete(`/expense-categories/${id}`, { headers: authHeaders() }),
  list: (params) => api.get('/expenses', { params, headers: authHeaders() }).then((r) => r.data),
  create: (data) =>
    api
      .post('/expenses', {
        category_id: data.categoryId,
        amount: Number(data.amount),
        date: data.date,
        comment: data.comment || '',
      }, { headers: authHeaders() })
      .then((r) => r.data),
  update: (id, data) =>
    api
      .put(`/expenses/${id}`, {
        category_id: data.categoryId,
        amount: Number(data.amount),
        date: data.date,
        comment: data.comment,
      }, { headers: authHeaders() })
      .then((r) => r.data),
  delete: (id) => api.delete(`/expenses/${id}`, { headers: authHeaders() }),
};

export const additionalIncomesApi = {
  list: (params) =>
    api.get('/additional-incomes', { params, headers: authHeaders() }).then((r) => r.data),
  create: (data) =>
    api
      .post(
        '/additional-incomes',
        {
          amount: Number(data.amount),
          date: data.date,
          title: data.title || '',
          comment: data.comment || '',
        },
        { headers: authHeaders() }
      )
      .then((r) => r.data),
  update: (id, data) =>
    api
      .put(
        `/additional-incomes/${id}`,
        {
          amount: Number(data.amount),
          date: data.date,
          title: data.title || '',
          comment: data.comment || '',
        },
        { headers: authHeaders() }
      )
      .then((r) => r.data),
  delete: (id) => api.delete(`/additional-incomes/${id}`, { headers: authHeaders() }),
};

export const mailingsApi = {
  settings: () => api.get('/mailings/settings', { headers: authHeaders() }).then((r) => r.data),
  updateSettings: (data) =>
    api
      .put('/mailings/settings', {
        green_instance_id: data.greenInstanceId,
        green_api_token: data.greenApiToken,
        green_api_host: data.greenApiHost,
        whatsapp_connected: data.whatsappConnected,
        whatsapp_bot_enabled: data.whatsappBotEnabled,
        whatsapp_bot_ai_enabled: data.whatsappBotAiEnabled,
        whatsapp_bot_welcome_message: data.whatsappBotWelcomeMessage,
        whatsapp_bot_off_hours_message: data.whatsappBotOffHoursMessage,
        whatsapp_bot_faq: data.whatsappBotFaq,
        auto_send_enabled: data.autoSendEnabled,
        auto_send_hour: data.autoSendHour,
        triggers: data.triggers,
        trigger_messages: data.triggerMessages,
        course_visit_reminders_enabled: data.courseVisitRemindersEnabled,
        course_visit_messages: data.courseVisitMessages,
      }, { headers: authHeaders() })
      .then((r) => r.data),
  botReport: (days = 7) =>
    api.get('/mailings/bot-report', { params: { days }, headers: authHeaders() }).then((r) => r.data),
  testGreenApi: (data) =>
    api
      .post(
        '/mailings/test-green-api',
        {
          green_instance_id: data.greenInstanceId,
          green_api_token: data.greenApiToken,
          green_api_host: data.greenApiHost,
        },
        { headers: authHeaders() }
      )
      .then((r) => r.data),
  sendTestMessage: (data) =>
    api
      .post(
        '/mailings/send-test-message',
        {
          phone: data.phone,
          message: data.message,
          green_instance_id: data.greenInstanceId,
          green_api_token: data.greenApiToken,
          green_api_host: data.greenApiHost,
        },
        { headers: authHeaders() }
      )
      .then((r) => r.data),
  applyGreenWebhook: () =>
    api.post('/mailings/apply-green-webhook', {}, { headers: authHeaders() }).then((r) => r.data),
  testBotMessage: (data) =>
    api
      .post(
        '/mailings/test-bot-message',
        {
          phone: data.phone,
          message: data.message,
        },
        { headers: authHeaders() }
      )
      .then((r) => r.data),
  templates: () => api.get('/mailings/templates', { headers: authHeaders() }).then((r) => r.data),
  createTemplate: (data) =>
    api
      .post('/mailings/templates', {
        name: data.name,
        audience: data.audience,
        text: data.text,
        enabled: data.enabled ?? false,
        recipient_client_ids:
          data.audience === 'selected' && Array.isArray(data.recipientClientIds)
            ? data.recipientClientIds
            : null,
      }, { headers: authHeaders() })
      .then((r) => r.data),
  updateTemplate: (id, data) =>
    api
      .put(`/mailings/templates/${id}`, {
        name: data.name,
        audience: data.audience,
        text: data.text,
        enabled: data.enabled,
        recipient_client_ids:
          data.audience === 'selected' && Array.isArray(data.recipientClientIds)
            ? data.recipientClientIds
            : null,
      }, { headers: authHeaders() })
      .then((r) => r.data),
};

export const additionalSalesApi = {
  categories: () => api.get('/additional-sale-categories', { headers: authHeaders() }).then((r) => r.data),
  list: (params) => api.get('/additional-sales', { params, headers: authHeaders() }).then((r) => r.data),
  create: (data) =>
    api
      .post('/additional-sales', {
        category_id: data.categoryId,
        product_id: data.productId || null,
        name: data.name || 'Продажа',
        amount: Number(data.amount),
        quantity: Number(data.quantity) || 1,
        date: data.date,
        comment: data.comment || '',
      }, { headers: authHeaders() })
      .then((r) => r.data),
  update: (id, data) =>
    api
      .put(`/additional-sales/${id}`, {
        category_id: data.categoryId,
        name: data.name,
        amount: Number(data.amount),
        quantity: Number(data.quantity),
        date: data.date,
        comment: data.comment,
      }, { headers: authHeaders() })
      .then((r) => r.data),
  delete: (id) => api.delete(`/additional-sales/${id}`, { headers: authHeaders() }),
};

export const dashboardApi = {
  stats: (params) => api.get('/dashboard/stats', { params, headers: authHeaders() }).then((r) => r.data),
  medicalRecordClosureReport: (params) =>
    api
      .get('/medical-records/closure-report', { params, headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
};

export const reportsApi = {
  clinic: (params) =>
    api
      .get('/reports/clinic', { params, headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  monthlyMatrix: (params) =>
    api
      .get('/reports/monthly-matrix', { params, headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  doctorWorkload: (params) =>
    api
      .get('/reports/doctor-workload', { params, headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
};

export const staffPaymentsApi = {
  list: (params) =>
    api
      .get('/staff-payments', { params: params || {}, headers: authHeaders() })
      .then((r) => {
        const list = r.data?.data != null ? r.data.data : r.data;
        return (Array.isArray(list) ? list : []).map((p) => ({
          ...p,
          masterName: p.masterName ?? p.doctor_name ?? p.doctorName,
          serviceName: p.serviceName ?? p.service_name,
          clientName: p.clientName ?? p.client_name,
          clientPhone: p.clientPhone ?? p.client_phone,
          paymentMethod: p.paymentMethod ?? p.payment_method,
          masterPayout: p.masterPayout ?? p.doctor_payout ?? p.doctorPayout,
          clinicIncome: p.clinicIncome ?? p.clinic_income,
          recordedByName: p.recordedByName ?? p.recorded_by_name,
          refundedAmount: p.refundedAmount ?? p.refunded_amount,
        }));
      }),
  create: (data) =>
    api
      .post('/staff-payments', data, { headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  update: (id, data) =>
    api
      .put(`/staff-payments/${id}`, data, { headers: authHeaders() })
      .then((r) => {
        const p = parseJsonResponse(r?.data ?? r);
        return {
          ...p,
          masterName: p.masterName ?? p.doctor_name ?? p.doctorName,
          serviceName: p.serviceName ?? p.service_name,
          clientName: p.clientName ?? p.client_name,
          clientPhone: p.clientPhone ?? p.client_phone,
          paymentMethod: p.paymentMethod ?? p.payment_method,
          masterPayout: p.masterPayout ?? p.doctor_payout ?? p.doctorPayout,
          clinicIncome: p.clinicIncome ?? p.clinic_income,
          recordedByName: p.recordedByName ?? p.recorded_by_name,
          refundedAmount: p.refundedAmount ?? p.refunded_amount,
        };
      }),
  delete: (id) => api.delete(`/staff-payments/${id}`, { headers: authHeaders() }),
  refund: (id, data) =>
    api
      .post(`/staff-payments/${id}/refund`, data, { headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  settle: (id, data) =>
    api
      .post(`/staff-payments/${id}/settle`, data, { headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
};

export const receptionShiftsApi = {
  current: () =>
    api.get('/reception-shifts/current', { headers: authHeaders() }).then((r) => r.data?.shift ?? null),
  currentContext: () =>
    api.get('/reception-shifts/current', { headers: authHeaders() }).then((r) => ({
      shift: r.data?.shift ?? null,
      paymentQrUrl: r.data?.paymentQrUrl ?? r.data?.payment_qr_url ?? null,
      clinic: r.data?.clinic ?? null,
    })),
  open: () =>
    api.post('/reception-shifts/open', {}, { headers: authHeaders() }).then((r) => r.data?.shift ?? null),
  close: (id) =>
    api.post(`/reception-shifts/${id}/close`, {}, { headers: authHeaders() }).then((r) => r.data?.shift ?? null),
  list: (params) =>
    api
      .get('/reception-shifts', { params: params || {}, headers: authHeaders() })
      .then((r) => r.data?.items ?? []),
  get: (id) =>
    api.get(`/reception-shifts/${id}`, { headers: authHeaders() }).then((r) => r.data?.shift ?? null),
  payments: (id, params) =>
    api
      .get(`/reception-shifts/${id}/payments`, { params: params || {}, headers: authHeaders() })
      .then((r) => r.data),
  daySummary: (date) =>
    api
      .get('/reception-shifts/day-summary', { params: { date }, headers: authHeaders() })
      .then((r) => r.data),
  dailyReports: (params) =>
    api
      .get('/reception-shifts/daily-reports', { params: params || {}, headers: authHeaders() })
      .then((r) => r.data),
};

export const doctorShiftsApi = {
  current: () =>
    api.get('/doctor-shifts/current', { headers: authHeaders() }).then((r) => r.data?.shift ?? null),
  open: () =>
    api.post('/doctor-shifts/open', {}, { headers: authHeaders() }).then((r) => r.data?.shift ?? null),
  close: (id) =>
    api.post(`/doctor-shifts/${id}/close`, {}, { headers: authHeaders() }).then((r) => r.data?.shift ?? null),
  list: (params) =>
    api
      .get('/doctor-shifts', { params: params || {}, headers: authHeaders() })
      .then((r) => r.data?.items ?? []),
  get: (id) =>
    api.get(`/doctor-shifts/${id}`, { headers: authHeaders() }).then((r) => r.data?.shift ?? null),
  payments: (id, params) =>
    api
      .get(`/doctor-shifts/${id}/payments`, { params: params || {}, headers: authHeaders() })
      .then((r) => r.data),
  dailyReports: (params) =>
    api
      .get('/reception-shifts/doctor-daily-reports', { params: params || {}, headers: authHeaders() })
      .then((r) => r.data),
};

export const doctorUnavailabilityApi = {
  list: (params) =>
    api
      .get('/doctor-unavailabilities', { params: params || {}, headers: authHeaders() })
      .then((r) => r.data),
  create: (data) =>
    api
      .post(
        '/doctor-unavailabilities',
        {
          doctor_id: data.doctorId,
          date: data.date,
          time_from: data.timeFrom || null,
          time_to: data.timeTo || null,
          reason: data.reason || null,
        },
        { headers: authHeaders() }
      )
      .then((r) => r.data),
  remove: (id) =>
    api.delete(`/doctor-unavailabilities/${id}`, { headers: authHeaders() }).then((r) => r.data),
};

export const productsApi = {
  list: () => api.get('/products', { headers: authHeaders() }).then((r) => r.data),
  create: (data) =>
    api.post('/products', {
      name: data.name,
      category: data.category || '',
      price: Number(data.price) || 0,
      stock: Number(data.stock) || 0,
      unit: data.unit || 'шт',
      min_stock: Number(data.minStock ?? data.min_stock ?? 5) || 5,
      comment: data.comment || '',
    }, { headers: authHeaders() }).then((r) => r.data),
  update: (id, data) =>
    api.put(`/products/${id}`, {
      name: data.name,
      category: data.category || '',
      price: Number(data.price) || 0,
      stock: Number(data.stock) || 0,
      unit: data.unit || 'шт',
      min_stock: Number(data.minStock ?? data.min_stock ?? 5) || 5,
      comment: data.comment || '',
    }, { headers: authHeaders() }).then((r) => r.data),
  delete: (id) => api.delete(`/products/${id}`, { headers: authHeaders() }),
};

export const pharmacyApi = {
  movements: (params) =>
    api
      .get('/pharmacy/movements', { params, headers: authHeaders() })
      .then((r) => r.data),
  receive: (data) =>
    api
      .post('/pharmacy/movements', {
        product_id: data.productId,
        quantity: Number(data.quantity) || 1,
        unit_cost: data.unitCost != null ? Number(data.unitCost) : null,
        date: data.date,
        comment: data.comment || null,
        movement_type: 'intake',
      }, { headers: authHeaders() })
      .then((r) => r.data),
  dispense: (data) =>
    api
      .post('/pharmacy/movements', {
        product_id: data.productId,
        quantity: Number(data.quantity) || 1,
        unit_cost: data.unitCost != null ? Number(data.unitCost) : null,
        date: data.date,
        comment: data.comment || null,
        movement_type: 'dispense',
        usage_area: data.usageArea || null,
        booking_id: data.bookingId || null,
        client_id: data.clientId || null,
      }, { headers: authHeaders() })
      .then((r) => r.data),
  dispenseBatch: (data) =>
    api
      .post('/pharmacy/movements/batch', {
        items: (Array.isArray(data.items) ? data.items : []).map((item) => ({
          product_id: item.productId,
          quantity: Number(item.quantity) || 1,
          unit_cost: item.unitCost != null ? Number(item.unitCost) : null,
        })),
        date: data.date || null,
        comment: data.comment || null,
        usage_area: data.usageArea || null,
        booking_id: data.bookingId || null,
        client_id: data.clientId || null,
      }, { headers: authHeaders() })
      .then((r) => r.data),
  summary: (params) =>
    api
      .get('/pharmacy/movements/summary', { params, headers: authHeaders() })
      .then((r) => r.data),
  balance: (params) =>
    api
      .get('/pharmacy/movements/balance', { params, headers: authHeaders() })
      .then((r) => r.data),
};

export const subscriptionsApi = {
  packages: () => api.get('/subscription-packages', { headers: authHeaders() }).then((r) => r.data),
  createPackage: (data) =>
    api
      .post('/subscription-packages', {
        name: data.name,
        visits: Number(data.visits),
        price: Number(data.price),
      }, { headers: authHeaders() })
      .then((r) => r.data),
  list: () => api.get('/subscriptions', { headers: authHeaders() }).then((r) => r.data),
  create: (data) =>
    api
      .post('/subscriptions', {
        client_id: data.clientId,
        package_id: data.packageId,
        total_visits: Number(data.totalVisits),
        remaining: Number(data.remaining),
        purchase_date: data.purchaseDate,
      }, { headers: authHeaders() })
      .then((r) => r.data),
  update: (id, data) =>
    api
      .put(`/subscriptions/${id}`, {
        client_id: data.clientId,
        package_id: data.packageId,
        total_visits: data.totalVisits,
        remaining: data.remaining,
        purchase_date: data.purchaseDate,
      }, { headers: authHeaders() })
      .then((r) => r.data),
  delete: (id) => api.delete(`/subscriptions/${id}`, { headers: authHeaders() }),
};

export const settingsApi = {
  get: () =>
    api.get('/settings', { headers: authHeaders() }).then((r) => normalizeSettingsResponse(r.data)),
  update: (data) => {
    const payload = {};
    if (data && typeof data === 'object') {
      if (data.adminPercent !== undefined) payload.admin_percent = data.adminPercent;
      if (data.paymentQrUrl !== undefined) payload.payment_qr_url = data.paymentQrUrl;
      if (data.clinicName !== undefined) payload.clinic_name = data.clinicName;
      if (data.clinicInn !== undefined) payload.clinic_inn = data.clinicInn;
      if (data.clinicAddress !== undefined) payload.clinic_address = data.clinicAddress;
      if (data.symptomOptions !== undefined) payload.symptom_options = data.symptomOptions;
      if (data.symptomRules !== undefined) payload.symptom_rules = data.symptomRules;
      if (data.monthlyRevenueGoal !== undefined) payload.monthly_revenue_goal = data.monthlyRevenueGoal;
    }
    return api
      .put('/settings', payload, { headers: authHeaders() })
      .then((r) => normalizeSettingsResponse(r.data));
  },
  uploadPaymentQr: (file) => {
    const form = new FormData();
    form.append('image', file);
    return api
      .post('/settings/payment-qr', form, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },
};

export const scheduleApi = {
  config: () => api.get('/schedule/config', { headers: authHeaders() }).then((r) => r.data),
};

export const icd10Api = {
  search: (q, limit = 25) =>
    api
      .get('/icd10/search', { params: { q, limit }, headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  resolve: (code) =>
    api
      .get('/icd10/resolve', { params: { code }, headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  diagnosisTypes: () =>
    api.get('/icd10/diagnosis-types', { headers: authHeaders() }).then((r) => parseJsonResponse(r?.data ?? r)),
};

export const medicalTemplatesApi = {
  list: (params) =>
    api
      .get('/medical-templates', { params, headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  create: (data) =>
    api
      .post(
        '/medical-templates',
        {
          name: data.name,
          disease_label: data.diseaseLabel || null,
          field_key: data.fieldKey || null,
          form_type: data.formType || null,
          sort_order: data.sortOrder ?? 0,
          payload: data.payload,
        },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
  update: (id, data) =>
    api
      .put(
        `/medical-templates/${id}`,
        {
          name: data.name,
          disease_label: data.diseaseLabel,
          field_key: data.fieldKey,
          form_type: data.formType,
          sort_order: data.sortOrder,
          payload: data.payload,
        },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
  remove: (id) =>
    api.delete(`/medical-templates/${id}`, { headers: authHeaders() }).then((r) => parseJsonResponse(r?.data ?? r)),
};

export const medicalStudyItemsApi = {
  catalog: () =>
    api.get('/medical-study-items', { headers: authHeaders() }).then((r) => parseJsonResponse(r?.data ?? r)),
  create: (data) =>
    api
      .post(
        '/medical-study-items',
        {
          category: data.category,
          code: data.code || null,
          name: data.name,
          name_short: data.nameShort || null,
          sort_order: data.sortOrder ?? 0,
          is_active: data.isActive !== false,
        },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
  update: (id, data) =>
    api
      .put(
        `/medical-study-items/${id}`,
        {
          category: data.category,
          code: data.code,
          name: data.name,
          name_short: data.nameShort,
          sort_order: data.sortOrder,
          is_active: data.isActive,
        },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
  remove: (id) =>
    api.delete(`/medical-study-items/${id}`, { headers: authHeaders() }).then((r) => parseJsonResponse(r?.data ?? r)),
};

export const referralTemplatesApi = {
  list: () =>
    api.get('/referral-templates', { headers: authHeaders() }).then((r) => parseJsonResponse(r?.data ?? r)),
  create: (data) =>
    api
      .post(
        '/referral-templates',
        {
          name: data.name,
          title_legal: data.titleLegal || null,
          html_body: data.htmlBody,
          is_default: !!data.isDefault,
          sort_order: data.sortOrder ?? 0,
        },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
  update: (id, data) =>
    api
      .put(`/referral-templates/${id}`, data, { headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  remove: (id) =>
    api.delete(`/referral-templates/${id}`, { headers: authHeaders() }).then((r) => parseJsonResponse(r?.data ?? r)),
};

export const bookingStudiesApi = {
  get: (bookingId) =>
    api
      .get(`/bookings/${bookingId}/medical-studies`, { headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  sync: (bookingId, itemIds) =>
    api
      .put(
        `/bookings/${bookingId}/medical-studies`,
        { item_ids: itemIds },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
  referralDocument: (bookingId, { itemIds, templateId, purpose }) =>
    api
      .post(
        `/bookings/${bookingId}/referral-document`,
        {
          item_ids: itemIds,
          template_id: templateId || null,
          purpose: purpose || null,
        },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
};

export const bookingPharmacyApi = {
  list: (bookingId) =>
    api
      .get(`/bookings/${bookingId}/pharmacy-dispenses`, { headers: authHeaders() })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  dispense: (bookingId, data) =>
    api
      .post(
        `/bookings/${bookingId}/pharmacy-dispenses`,
        {
          product_id: data.productId,
          quantity: Number(data.quantity) || 1,
          comment: data.comment || null,
        },
        { headers: authHeaders() }
      )
      .then((r) => parseJsonResponse(r?.data ?? r)),
};

export const clinicApi = {
  meta: () => api.get('/clinic/meta', { headers: authHeaders() }).then((r) => r.data),
  analyzeSymptoms: (symptoms, freeText, options = {}) => {
    const list = Array.isArray(symptoms) ? symptoms.filter((s) => typeof s === 'string' && s.trim()) : [];
    const text = typeof freeText === 'string' ? freeText.trim() : '';
    return api
      .post(
        '/clinic/analyze-symptoms',
        {
          symptoms: list,
          free_text: text,
          ...(options.age != null && options.age !== '' ? { age: Number(options.age) } : {}),
          ...(options.mainDiagnosis ? { main_diagnosis: options.mainDiagnosis } : {}),
          ...(options.serviceName ? { service_name: options.serviceName } : {}),
        },
        { headers: authHeaders() }
      )
      .then((r) => r.data);
  },
  medicalRecordDraft: (symptoms, freeText, options = {}) => {
    const list = Array.isArray(symptoms) ? symptoms.filter((s) => typeof s === 'string' && s.trim()) : [];
    const text = typeof freeText === 'string' ? freeText.trim() : '';
    return api
      .post(
        '/clinic/medical-record-draft',
        {
          symptoms: list,
          free_text: text,
          ...(options.age != null && options.age !== '' ? { age: Number(options.age) } : {}),
          ...(options.mainDiagnosis ? { main_diagnosis: options.mainDiagnosis } : {}),
          ...(options.serviceName ? { service_name: options.serviceName } : {}),
          ...(options.allergies ? { allergies: options.allergies } : {}),
          ...(options.chronic ? { chronic: options.chronic } : {}),
          ...(options.notes ? { notes: options.notes } : {}),
        },
        { headers: authHeaders() }
      )
      .then((r) => r.data);
  },
  protocolTemplate: (options = {}) =>
    api
      .post(
        '/clinic/protocol-template',
        {
          service_name: options.serviceName || null,
          main_diagnosis: options.mainDiagnosis || null,
        },
        { headers: authHeaders() }
      )
      .then((r) => r.data),
};

export const notificationsApi = {
  list: () => api.get('/notifications', { headers: authHeaders() }).then((r) => r.data),
};

export const activityLogsApi = {
  list: (params = {}) =>
    api
      .get('/activity-logs', { params, headers: authHeaders() })
      .then((r) => (Array.isArray(r.data) ? r.data : r.data?.data ?? [])),
};

export async function downloadExport(type, { dateFrom, dateTo, year, monthFrom, monthTo } = {}) {
  const token = getToken();
  const params = new URLSearchParams();
  if (dateFrom) params.set('date_from', dateFrom);
  if (dateTo) params.set('date_to', dateTo);
  if (year) params.set('year', String(year));
  if (monthFrom) params.set('month_from', String(monthFrom));
  if (monthTo) params.set('month_to', String(monthTo));
  const qs = params.toString();
  const res = await fetch(`${API_BASE_URL}/exports/${type}${qs ? `?${qs}` : ''}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    throw new Error('Не удалось скачать файл');
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = res.headers.get('Content-Disposition')?.match(/filename="?([^"]+)/)?.[1] || `${type}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export const aiSettingsApi = {
  get: () => api.get('/settings/ai', { headers: authHeaders() }).then((r) => r.data),
  setEnabled: (symptomAiEnabled) =>
    api
      .put(
        '/settings/ai/enabled',
        { symptom_ai_enabled: symptomAiEnabled },
        { headers: authHeaders() }
      )
      .then((r) => r.data),
  addKey: (label, key) =>
    api
      .post('/settings/ai/keys', { label, key }, { headers: authHeaders() })
      .then((r) => r.data),
  removeKey: (id) =>
    api.delete(`/settings/ai/keys/${id}`, { headers: authHeaders() }).then((r) => r.data),
  reorderKeys: (ids) =>
    api
      .put('/settings/ai/keys/order', { ids }, { headers: authHeaders() })
      .then((r) => r.data),
  testKey: (id) =>
    api.post(`/settings/ai/keys/${id}/test`, {}, { headers: authHeaders() }).then((r) => r.data),
  testRawKey: (key) =>
    api.post('/settings/ai/keys/test', { key }, { headers: authHeaders() }).then((r) => r.data),
};

export const accessApi = {
  catalog: () =>
    api.get('/access/permissions', { headers: authHeaders() }).then((r) => parseJsonResponse(r?.data ?? r)),
  listUsers: () =>
    api.get('/access/users', { headers: authHeaders() }).then((r) => parseJsonResponse(r?.data ?? r)),
  createUser: (payload) =>
    api.post('/access/users', payload, { headers: authHeaders() }).then((r) => parseJsonResponse(r?.data ?? r)),
  updateUser: (id, payload) =>
    api.put(`/access/users/${id}`, payload, { headers: authHeaders() }).then((r) => parseJsonResponse(r?.data ?? r)),
  deleteUser: (id) =>
    api.delete(`/access/users/${id}`, { headers: authHeaders() }).then((r) => parseJsonResponse(r?.data ?? r)),
};

/** Публичное табло в зале ожидания */
export const publicDisplayApi = {
  bootstrap: () =>
    api.get('/public/display/bootstrap').then((r) => parseJsonResponse(r?.data ?? r)),
  poll: (afterId) =>
    api
      .get('/public/display/poll', { params: { after_id: afterId } })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  speakUrl: (text) => {
    const params = new URLSearchParams({ text: String(text || '') });
    return `${API_BASE_URL}/public/display/speak?${params}`;
  },
};

/** Публичные методы для киоска без токена */
export const publicKioskApi = {
  config: () => api.get('/public/kiosk/config').then((r) => parseJsonResponse(r?.data ?? r)),
  availability: (doctorId, date) =>
    api
      .get('/public/kiosk/availability', { params: { doctor_id: doctorId, date } })
      .then((r) => parseJsonResponse(r?.data ?? r)),
  book: (data) =>
    api
      .post('/public/kiosk/bookings', {
        full_name: data.fullName,
        phone: data.phone,
        doctor_id: data.doctorId,
        service_id: data.serviceId,
        date: data.date,
        time: data.time,
      })
      .then((r) => parseJsonResponse(r?.data ?? r)),
};

/** Публичная обратная связь с лендинга / QR */
export const publicFeedbackApi = {
  submit: (data) =>
    api
      .post('/public/feedback', {
        type: data.type,
        author_name: data.authorName || null,
        phone: data.phone || null,
        message: data.message,
        rating: data.rating ?? null,
        source: data.source || 'landing',
      })
      .then((r) => parseJsonResponse(r?.data ?? r)),
};

/** Входящие обращения пациентов (директор) */
export const clinicFeedbackApi = {
  list: (params) =>
    api.get('/feedback/inbox', { params }).then((r) => parseJsonResponse(r?.data ?? r)),
  unreadCount: () =>
    api.get('/feedback/inbox/unread-count').then((r) => parseJsonResponse(r?.data ?? r)),
  update: (id, data) =>
    api.patch(`/feedback/inbox/${id}`, data).then((r) => parseJsonResponse(r?.data ?? r)),
};
